import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import { connect } from 'react-redux';

import EndOfReport from './EndOfReport';
import InterpretTestResult from './InterpretTestResult';
import SuggestedGuidelines from './SuggestedGuidelines';
import UncertaintyCalculation from './UncertaintyCalculation';
import GraphSettings from './GraphSettings';
import HourlyReadingChartSettings from './HourlyReadingChartSettings';

import Page from '../shared/Page';
import SaveButton from '../shared/SaveButton';
import CancelButton from '../shared/CancelButton';
import withRoleRestrictions from '../shared/withRoleRestrictions';
import { notifyError, notifyInfo } from '../../utils/notifier';
import { getBreadCrumbs } from '../../utils/breadCrumbs';
import withEulaRestrictions from '../license/withEulaRestrictions';
import { ADMINS } from '../../constants/userRoles';
import { PAGE_PATHS, REPORT_MANAGEMENT } from '../../constants/pathsRoots';
import { INTERPRETATION_OF_RESULT, SUGGESTION_GUIDLINE_FOR_RETESTING } from '../../constants/reportContent';
import {
  fetchReportOptions,
  updateReportOptions,
  uploadReportAttachment,
  removeReportAttachment,
  downloadReportAttachment,
} from '../../actions/companies/apiRequests';
import Attachment from './Attachment';
import Modal from '../shared/Modal';

const REPORT_LAYOUT = 'Official';
const REPORT_TEMPLATE = 'Standard';

class ReportContentLayout extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasAttachment: false,
      attachment: null,
      attachmentDeleted: false,
      saving: false,
      interpretRadonTestResult: '',
      retestingGuidelines: '',
      endOfReport: '',
      initInterpretRadonTestResult: '',
      initRetestingGuidelines: '',
      initEndOfReport: '',
      contentSettings: {
        uncertaintyCalculation: false,
        graphIncludeAdditionalSensor: true,
        graphRadonAverageLineEnabled: true,
        hourlyDisplayChart: true,
        includeTimeStamp: false,
      },
      reportOptions: {},
      layoutTemplateId: '',
      errors: {},
      attachmentFilename: '',
    };

    this.onFieldChange = this.onFieldChange.bind(this);
    this.onContentChange = this.onContentChange.bind(this);
    this.onSave = this.onSave.bind(this);
    this.uploadAttachment = this.uploadAttachment.bind(this);
    this.removeAttachment = this.removeAttachment.bind(this);
    this.downloadAttachment = this.downloadAttachment.bind(this);
    this.showModalError = this.showModalError.bind(this);
    this.hideAttachmentUploadError = this.hideAttachmentUploadError.bind(this);
    this.onValidate = this.onValidate.bind(this);

    this.reportType = 'Official';
  }

  static get propTypes() {
    return { history: PropTypes.object, match: PropTypes.object, uom: PropTypes.string };
  }

  componentDidMount() {
    const { match } = this.props;
    fetchReportOptions(match.params.companyId).then(
      ({
        interpretRadonTestResult,
        retestingGuidelines,
        endOfReport,
        contentSettings,
        layoutTemplateId,
        hasAttachment,
        attachmentFilename,
        ...res
      }) => {
        this.setState({
          initInterpretRadonTestResult: interpretRadonTestResult || INTERPRETATION_OF_RESULT,
          interpretRadonTestResult: interpretRadonTestResult || INTERPRETATION_OF_RESULT,
          initRetestingGuidelines: retestingGuidelines || SUGGESTION_GUIDLINE_FOR_RETESTING,
          retestingGuidelines: retestingGuidelines || SUGGESTION_GUIDLINE_FOR_RETESTING,
          initEndOfReport: endOfReport || '',
          endOfReport: endOfReport || '',
          contentSettings: (!!contentSettings && {
            uncertaintyCalculation: contentSettings.uncertaintyCalculation,
            graphIncludeAdditionalSensor: contentSettings.graphIncludeAdditionalSensor,
            graphRadonAverageLineEnabled: contentSettings.graphRadonAverageLineEnabled,
            hourlyDisplayChart: contentSettings.hourlyDisplayChart,
            includeTimeStamp: contentSettings.includeTimeStamp,
          }) || {
            uncertaintyCalculation: false,
            graphIncludeAdditionalSensor: true,
            graphRadonAverageLineEnabled: true,
            hourlyDisplayChart: true,
            includeTimeStamp: false,
          },
          layoutTemplateId,
          reportOptions: res,
          hasAttachment,
          attachmentFilename,
        });
      },
    );
  }

  actions() {
    const { saving, errors } = this.state;
    const hasErrors = Object.values(errors).filter((value) => value).length;
    const {
      match: {
        params: { companyId },
      },
    } = this.props;

    return [
      <CancelButton
        key="cancel"
        onClick={() =>
          this.props.history.push({
            pathname: '/reports',
            state: {
              companyId,
            },
          })
        }
      />,
      <SaveButton key="save" onClick={this.onSave} loading={saving} disabled={saving || hasErrors > 0} />,
    ];
  }

  onSave() {
    const { match, uom } = this.props;
    const {
      reportOptions,
      interpretRadonTestResult,
      retestingGuidelines,
      endOfReport,
      contentSettings,
      layoutTemplateId,
    } = this.state;
    this.setState({ saving: true });

    updateReportOptions(match.params.companyId, {
      ...reportOptions,
      unitOfMeasure: uom,
      interpretRadonTestResult,
      retestingGuidelines,
      endOfReport,
      layoutTemplateId,
      contentSettings,
    })
      .then(() => {
        this.updateAttachment()
          .then(() => {
            notifyInfo();
            this.setState({ saving: false });
          })
          .catch((error) => {
            notifyError(error.message);
            this.setState({ saving: false });
          });
      })
      .catch((error) => {
        notifyError(error.message);
        this.setState({ saving: false });
      });
  }

  onValidate(name, value) {
    const isInvalid = value === null && name !== 'endOfReport';
    this.setState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [name]: isInvalid,
      },
    }));
  }

  onFieldChange(name, value) {
    this.onValidate(name, value);
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  onContentChange(name, value) {
    this.setState((prevState) => ({
      ...prevState.test,
      contentSettings: {
        ...prevState.contentSettings,
        [name]: value,
      },
    }));
  }

  uploadAttachment(files, rejectedFiles = []) {
    const rejectedFile = rejectedFiles[0];

    if (rejectedFile) {
      this.setState({
        fileUploadError: true,
      });
      return;
    }

    if (files && files[0]) {
      if (this.state.attachment) {
        this.setState({
          newAttachment: files[0],
          attachmentCheck: true,
        });
        return;
      }
      this.setState({
        attachment: files[0],
      });
    }
  }

  removeAttachment() {
    this.setState(({ hasAttachment }) => ({
      attachment: null,
      attachmentDeleted: hasAttachment,
    }));
  }

  downloadAttachment() {
    const { match } = this.props;
    const { reportOptions } = this.state;

    return downloadReportAttachment(match.params.companyId, REPORT_LAYOUT, REPORT_TEMPLATE);
  }

  updateAttachment() {
    const { match } = this.props;
    const { attachment, attachmentDeleted, reportOptions } = this.state;

    if (attachment) {
      return uploadReportAttachment(match.params.companyId, attachment, REPORT_LAYOUT, REPORT_TEMPLATE);
    }

    if (attachmentDeleted) {
      return removeReportAttachment(match.params.companyId, REPORT_LAYOUT, REPORT_TEMPLATE);
    }

    return Promise.resolve();
  }

  showModalError() {
    this.setState({
      fileUploadError: true,
    });
  }

  hideAttachmentUploadError() {
    this.setState({
      fileUploadError: false,
    });
  }

  render() {
    const {
      initInterpretRadonTestResult,
      initRetestingGuidelines,
      initEndOfReport,
      attachment,
      hasAttachment,
      attachmentDeleted,
      attachmentFilename,
      fileUploadError,
      errors,
    } = this.state;
    const {
      uncertaintyCalculation,
      graphIncludeAdditionalSensor,
      graphRadonAverageLineEnabled,
      hourlyDisplayChart,
      includeTimeStamp,
    } = this.state.contentSettings;
    return (
      <Page
        header="Edit Official Report Content"
        description="The settings on this page will be included each time the report template is generated"
        actions={this.actions()}
        breadCrumbs={getBreadCrumbs('Edit Report', location.pathname, [PAGE_PATHS[REPORT_MANAGEMENT]])}
      >
        <Row>
          <Col md={8}>
            <div className="mb-3">
              <InterpretTestResult
                value={initInterpretRadonTestResult}
                onChange={this.onFieldChange}
                error={errors.interpretRadonTestResult}
              />
            </div>
            <div className="mb-3">
              <SuggestedGuidelines
                value={initRetestingGuidelines}
                onChange={this.onFieldChange}
                error={errors.retestingGuidelines}
              />
            </div>
            <div className="mb-3">
              <EndOfReport value={initEndOfReport} onChange={this.onFieldChange} />
            </div>
          </Col>
          <Col md={4} className="">
            {false && (
              <UncertaintyCalculation
                description="The Uncertainty Calculation is lorem ipsum dolor sit amet, consectetur"
                isEnable={uncertaintyCalculation ? uncertaintyCalculation : false}
                onChange={this.onContentChange}
              />
            )}
            <GraphSettings
              includeAdditionlGraph={graphIncludeAdditionalSensor}
              enableRadonAverage={graphRadonAverageLineEnabled}
              onChange={this.onContentChange}
            />
            <HourlyReadingChartSettings
              displayChart={hourlyDisplayChart}
              includeTimeStamp={includeTimeStamp}
              onChange={this.onContentChange}
            />
            <Attachment
              hasAttachment={hasAttachment}
              file={attachment}
              attachmentDeleted={attachmentDeleted}
              onUpload={this.uploadAttachment}
              onRemove={this.removeAttachment}
              downloadAttachment={this.downloadAttachment}
              attachmentFilename={attachmentFilename}
              modalError={this.showModalError}
            />
            <Modal
              isOpen={fileUploadError}
              showCancelButton={false}
              onConfirm={this.hideAttachmentUploadError}
              header="File upload error"
            >
              File cannot be uploaded. Make sure that file extension is PDF and its size less than 4Mb
            </Modal>
          </Col>
        </Row>
      </Page>
    );
  }
}

const mapStateToProps = ({ profile }) => ({
  uom: profile.uom,
});

export default connect(mapStateToProps)(withEulaRestrictions(withRoleRestrictions(ReportContentLayout, ADMINS)));



// WEBPACK FOOTER //
// src/components/reportContentLayout/ReportContentLayout.jsx